// ─── 질의 현황 섹션 ───────────────────────────────────────────────────────────
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Calendar, User, Search, RotateCcw,
  ChevronDown, ChevronUp, ArrowUpDown, ArrowUp, ArrowDown,
  FileText, Trash2,
} from 'lucide-react';
import { PER_PAGE, STATUS_KEYS, STATUS_META } from '../../constants/adminConstants';
import { fmtTime, fmtMs } from '../../utils/formatters';
import { qnaApi } from '../../../services/qnaApi';
import StatusBadge       from '../shared/StatusBadge';
import ScoreBar          from '../shared/ScoreBar';
import DetailModal       from '../modals/DetailModal';
import DeleteConfirmModal from '../modals/DeleteConfirmModal';

// ── 정렬 아이콘 ───────────────────────────────────────────────────────────────
function SortIcon({ field, sortBy, sortDir }) {
  if (sortBy !== field) return <ArrowUpDown size={13} className="text-gray-300" />;
  return sortDir === 'asc'
    ? <ArrowUp   size={13} className="text-sb-primary" />
    : <ArrowDown size={13} className="text-sb-primary" />;
}

// ── 테이블 컬럼 정의 ──────────────────────────────────────────────────────────
const COLUMNS = [
  ['chat_time',     '일시'],
  ['company_code',  '회사/부서'],
  ['user_name',     '이름'],
  ['question',      '질문'],
  ['answer_status', '상태'],
  ['eval_score',    '점수'],
  ['latency_ms',    '응답시간'],
  [null,            ''],
];

/**
 * 질의 현황 섹션 – 필터, 정렬, 페이지네이션, 행 확장, 상세 모달, 삭제를 포함합니다.
 * @param {{ rows: Array, refetch: function }} props
 */
export default function InquiriesSection({ rows, refetch }) {
  // ── 필터 상태 ──────────────────────────────────────────────────────────────
  const [nameTerm,    setNameTerm]    = useState('');
  const [contentTerm, setContentTerm] = useState('');
  const [startDate,   setStartDate]   = useState('');
  const [endDate,     setEndDate]     = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [applied, setApplied] = useState({ name: '', content: '', start: '', end: '' });

  // ── 페이지·정렬·확장 상태 ────────────────────────────────────────────────
  const [page,    setPage]    = useState(1);
  const [expanded, setExpanded] = useState(() => new Set());
  const [sortBy,  setSortBy]  = useState('chat_time');
  const [sortDir, setSortDir] = useState('desc');

  // ── 모달 상태 ──────────────────────────────────────────────────────────────
  const [detail,          setDetail]          = useState(null);
  const [deleteConfirmRow, setDeleteConfirmRow] = useState(null);
  const [isDeleting,      setIsDeleting]      = useState(false);

  // ── 삭제 처리 ──────────────────────────────────────────────────────────────
  const handleConfirmDelete = async () => {
    if (!deleteConfirmRow?.turn_id || !refetch) return;
    const tid = deleteConfirmRow.turn_id;
    setIsDeleting(true);
    try {
      await qnaApi.adminHistoryDelete(tid);
      toast.success('삭제되었습니다.');
      setDeleteConfirmRow(null);
      setDetail((d) => (d?.turn_id === tid ? null : d));
      await refetch();
    } catch {
      /* axios 인터셉터에서 오류 토스트 처리 */
    } finally {
      setIsDeleting(false);
    }
  };

  // ── 필터 적용 ──────────────────────────────────────────────────────────────
  const applySearch = () => {
    setApplied({ name: nameTerm, content: contentTerm, start: startDate, end: endDate });
    setPage(1);
  };

  const resetSearch = () => {
    setNameTerm(''); setContentTerm(''); setStartDate(''); setEndDate('');
    setStatusFilter('all');
    setApplied({ name: '', content: '', start: '', end: '' });
    setPage(1);
  };

  // ── 파생 데이터 ────────────────────────────────────────────────────────────
  const statusCounts = useMemo(() => {
    const m = {};
    rows.forEach((r) => { m[r.answer_status] = (m[r.answer_status] || 0) + 1; });
    return m;
  }, [rows]);

  const filtered = useMemo(() => rows.filter((item) => {
    if (statusFilter !== 'all' && item.answer_status !== statusFilter) return false;
    if (applied.start && item.chat_time?.slice(0, 10) < applied.start)   return false;
    if (applied.end   && item.chat_time?.slice(0, 10) > applied.end)     return false;
    if (applied.name) {
      const q   = applied.name.toLowerCase();
      const hit = item.user_name?.toLowerCase().includes(q)    ||
                  item.user_id?.toLowerCase().includes(q)      ||
                  item.company_name?.toLowerCase().includes(q) ||
                  item.company_code?.toLowerCase().includes(q);
      if (!hit) return false;
    }
    if (applied.content) {
      const c = applied.content.toLowerCase();
      if (!item.question?.toLowerCase().includes(c) && !item.answer?.toLowerCase().includes(c)) return false;
    }
    return true;
  }), [rows, statusFilter, applied]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av = a[sortBy], bv = b[sortBy];
      if (av == null) av = sortDir === 'asc' ? Infinity : -Infinity;
      if (bv == null) bv = sortDir === 'asc' ? Infinity : -Infinity;
      if (sortBy === 'chat_time') { av = new Date(av).getTime(); bv = new Date(bv).getTime(); }
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
    return arr;
  }, [filtered, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PER_PAGE));
  const paged      = sorted.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(field); setSortDir('desc'); }
    setPage(1);
  };

  const toggleExpand = (turnId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(turnId)) next.delete(turnId); else next.add(turnId);
      return next;
    });
  };

  const hasApplied = applied.name || applied.content || applied.start || applied.end;

  return (
    <div className="space-y-3">
      {/* ── 필터 패널 ── */}
      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
        {/* 상태 필터 탭 */}
        <div className="mb-3 flex flex-wrap gap-2">
          {STATUS_KEYS.map((s) => {
            const cnt    = s === 'all' ? rows.length : statusCounts[s] || 0;
            const m      = STATUS_META[s] || { label: s, dot: 'bg-slate-400', pill: 'bg-slate-100 text-slate-700' };
            const active = statusFilter === s;
            return (
              <button
                key={s} type="button"
                onClick={() => { setStatusFilter(s); setPage(1); }}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-[15px] font-semibold transition ${active ? `${m.pill} border-current` : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                {s !== 'all' && <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />}
                {s === 'all' ? '전체' : m.label}
                <span className="font-mono text-[14px] opacity-80">({cnt})</span>
              </button>
            );
          })}
        </div>

        {/* 검색 입력 */}
        <div className="flex flex-wrap items-center gap-2">
          {/* 기간 */}
          <div className="flex min-w-[200px] flex-1 self-center items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
            <Calendar size={14} className="shrink-0 text-gray-400" />
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="min-w-0 flex-1 border-0 bg-transparent text-base outline-none" />
            <span className="text-gray-400">~</span>
            <input type="date" value={endDate}   onChange={(e) => setEndDate(e.target.value)}   className="min-w-0 flex-1 border-0 bg-transparent text-base outline-none" />
          </div>
          {/* 이름/ID */}
          <div className="relative self-center">
            <User size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text" placeholder="이름/ID" value={nameTerm}
              onChange={(e) => setNameTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applySearch()}
              className="h-1 w-28 rounded-lg border border-gray-200 bg-white pl-9 pr-2 text-base outline-none focus:border-sb-primary"
            />
          </div>
          {/* 내용 검색 */}
          <div className="relative min-w-[160px] flex-1 self-center">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text" placeholder="질문 / 답변 검색" value={contentTerm}
              onChange={(e) => setContentTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applySearch()}
              className="h-1 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-2 text-base outline-none focus:border-sb-primary"
            />
          </div>
          <button type="button" onClick={applySearch}
            className="h-1 shrink-0 self-center rounded-lg bg-sb-primary px-5 text-base font-semibold text-white shadow-sm hover:bg-blue-700">
            조회
          </button>
          <button type="button" onClick={resetSearch}
            className="inline-flex h-1 shrink-0 self-center items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 text-base text-gray-600 hover:bg-gray-50">
            <RotateCcw size={12} />초기화
          </button>
        </div>

        {/* 적용 중인 필터 태그 */}
        {hasApplied && (
          <div className="mt-3 flex flex-wrap gap-2 border-t border-gray-100 pt-3 text-[15px] text-gray-500">
            <span>적용 중:</span>
            {applied.start   && <span className="rounded-md bg-blue-50 px-2 py-0.5 text-sb-primary">{applied.start}~{applied.end || '현재'}</span>}
            {applied.name    && <span className="rounded-md bg-blue-50 px-2 py-0.5 text-sb-primary">검색: {applied.name}</span>}
            {applied.content && <span className="rounded-md bg-blue-50 px-2 py-0.5 text-sb-primary">내용: {applied.content}</span>}
          </div>
        )}
      </div>

      {/* ── 건수 표시 ── */}
      <div className="flex justify-between px-1 text-base text-gray-500">
        <span>총 <strong className="text-gray-800">{sorted.length}</strong>건</span>
        <span className="text-gray-400">행 클릭 → 확장 · 전체 보기는 모달</span>
      </div>

      {/* ── 데이터 테이블 ── */}
      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[880px] border-collapse text-left">
            <thead>
              <tr className="border-b-2 border-gray-100">
                {COLUMNS.map(([k, label]) => (
                  <th
                    key={label || 'x'}
                    className="cursor-pointer whitespace-nowrap px-4 py-3 text-sm font-bold uppercase tracking-wide text-gray-400"
                    onClick={k ? () => toggleSort(k) : undefined}
                  >
                    <span className="inline-flex items-center gap-1">
                      {label}{k && <SortIcon field={k} sortBy={sortBy} sortDir={sortDir} />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.map((item) => {
                const open = expanded.has(item.turn_id);
                return (
                  <React.Fragment key={item.turn_id}>
                    {/* ── 기본 행 ── */}
                    <tr
                      className={`cursor-pointer border-b border-gray-50 transition hover:bg-gray-50 ${open ? 'bg-blue-50/40' : ''}`}
                      onClick={() => toggleExpand(item.turn_id)}
                    >
                      <td className="whitespace-nowrap px-4 py-3.5 font-mono text-base text-gray-600">{fmtTime(item.chat_time)}</td>
                      <td className="px-4 py-3.5">
                        <p className="text-base font-semibold text-gray-900">{item.company_name || item.company_code}</p>
                        <p className="text-sm text-gray-400">
                          {[item.company_name ? item.company_code : null, item.dept_name].filter(Boolean).join(' · ') || '—'}
                        </p>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3.5 text-base text-gray-600">{item.user_name}</td>
                      <td className="max-w-[220px] px-4 py-3.5">
                        <p className="truncate text-base text-gray-700">{item.question || <span className="text-gray-400">빈 질문</span>}</p>
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={item.answer_status} /></td>
                      <td className="px-4 py-3"><ScoreBar score={item.eval_score} compact /></td>
                      <td className="px-4 py-3.5 font-mono text-base text-gray-500">{fmtMs(item.latency_ms)}</td>
                      <td className="px-4 py-3 text-right text-gray-400">
                        <button type="button" title="삭제"
                          className="mr-2 inline-flex rounded-md p-1.5 text-red-500 transition hover:bg-red-50 hover:text-red-700"
                          onClick={(e) => { e.stopPropagation(); setDeleteConfirmRow(item); }}>
                          <Trash2 size={14} />
                        </button>
                        {open ? <ChevronUp size={14} className="inline align-middle" /> : <ChevronDown size={14} className="inline align-middle" />}
                      </td>
                    </tr>

                    {/* ── 확장 행 ── */}
                    {open && (
                      <tr className="border-b border-gray-100 bg-gray-50/80">
                        <td colSpan={8} className="px-4 py-4">
                          <div className="grid gap-3 md:grid-cols-2">
                            <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-3">
                              <p className="mb-2 text-sm font-bold uppercase text-sb-primary">질문</p>
                              <p className="text-base leading-relaxed text-gray-800">{item.question || '—'}</p>
                            </div>
                            <div className="rounded-xl border border-gray-200 bg-white p-3">
                              <p className="mb-2 text-sm font-bold uppercase text-gray-400">AI 답변</p>
                              <p className="line-clamp-4 text-base leading-relaxed text-gray-600">{item.answer || '—'}</p>
                            </div>
                          </div>
                          <div className="mt-3 flex flex-wrap justify-end gap-2">
                            <button type="button"
                              className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-white px-3 py-2 text-base font-semibold text-red-600 hover:bg-red-50"
                              onClick={(e) => { e.stopPropagation(); setDeleteConfirmRow(item); }}>
                              <Trash2 size={12} />삭제
                            </button>
                            <button type="button"
                              className="inline-flex items-center gap-1 text-base font-semibold text-sb-primary hover:text-blue-700"
                              onClick={(e) => { e.stopPropagation(); setDetail(item); }}>
                              <FileText size={12} />전체 보기
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {paged.length === 0 && <p className="py-14 text-center text-base text-gray-400">조회된 데이터가 없습니다</p>}

        {/* ── 페이지네이션 ── */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-1 border-t border-gray-100 py-3">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button key={p} type="button" onClick={() => setPage(p)}
                className={`inline-flex h-1 w-1 items-center justify-center rounded-lg text-sm font-semibold transition ${page === p ? 'bg-sb-primary text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
                {p}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── 모달 ── */}
      {detail && (
        <DetailModal
          row={detail}
          onClose={() => setDetail(null)}
          onDelete={(r) => { setDetail(null); setDeleteConfirmRow(r); }}
        />
      )}
      <DeleteConfirmModal
        row={deleteConfirmRow}
        onClose={() => !isDeleting && setDeleteConfirmRow(null)}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
}
